/** Tab-local command recovery. Captures intent once; never allocates a replacement operation. */
import { ApiError, archiveCoachResource, createCoachResource, duplicateCoachResource,
  isCurrentResourceCommandScope, saveCoachResourceRevision, updateCoachResourceMetadata } from './api';
import { invalidateResourceCommands } from './queryClient';
import type { ResourceCommand, ResourceCommandResult, ResourceReadScope } from './types';

export type ResourceCommandState =
  | { status: 'unavailable' | 'ready' | 'sending' | 'unknown' | 'rejected' }
  | { status: 'accepted'; result: ResourceCommandResult };

function acceptedResult(value: ResourceCommandResult, command: ResourceCommand): ResourceCommandResult {
  const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (!value || value.schema_version !== 'resource-result.v1' || !uuid.test(value.resource_id)
    || !uuid.test(value.revision_id) || !Number.isInteger(value.version) || value.version < 1
    || value.version > 2147483647 || Object.keys(value).sort().join(',') !== 'resource_id,revision_id,schema_version,version'
    || (command.action === 'create' || command.action === 'duplicate' ? value.version !== 1
      : value.version < command.body.expected_version || value.version > command.body.expected_version + 1)
    || (command.action !== 'create' && command.action !== 'duplicate'
      && value.resource_id.toLowerCase() !== command.resourceId.toLowerCase())) {
    throw new Error('invalid_resource_command_result');
  }
  return { ...value };
}

export function captureResourceCommand(scope: ResourceReadScope, command: ResourceCommand,
  options: { restoredUnknown?: boolean } = {}) {
  const capturedScope = { ...scope };
  const serialized = JSON.stringify(command);
  let state: ResourceCommandState = { status: options.restoredUnknown ? 'unknown' : 'ready' };
  let unresolved = options.restoredUnknown ?? false;
  let inFlight: Promise<ResourceCommandResult> | null = null;
  const current = () => isCurrentResourceCommandScope(capturedScope);
  const intent = (): ResourceCommand => JSON.parse(serialized) as ResourceCommand;

  async function run(signal?: AbortSignal): Promise<ResourceCommandResult> {
    state = { status: 'sending' };
    const original = intent();
    const options = { scope: capturedScope, signal };
    try {
      const value = original.action === 'create' ? await createCoachResource(original.body, options)
        : original.action === 'save' ? await saveCoachResourceRevision(original.resourceId, original.body, options)
          : original.action === 'metadata' ? await updateCoachResourceMetadata(original.resourceId, original.body, options)
            : original.action === 'duplicate' ? await duplicateCoachResource(original.resourceId, original.body, options)
              : await archiveCoachResource(original.resourceId, original.body, options);
      const result = acceptedResult(value, original);
      unresolved = false;
      state = { status: 'accepted', result };
      // Cache refresh failure cannot turn an accepted independent save into a failed save.
      void invalidateResourceCommands(capturedScope).catch(() => {});
      return { ...result };
    } catch (error) {
      // Transport, cancellation, boundary departure and 5xx can follow commit.
      const code = error instanceof ApiError && error.detail && typeof error.detail === 'object'
        && 'code' in error.detail ? error.detail.code : null;
      if (code === 'resource_operation_retired'
        || !(error instanceof ApiError && error.status >= 400 && error.status < 500)) unresolved = true;
      // A denied later retry says nothing about whether an earlier unknown
      // attempt committed. Only successful reconciliation resolves that state.
      state = { status: unresolved ? 'unknown' : 'rejected' };
      throw error;
    } finally {
      inFlight = null;
    }
  }

  return {
    adoptUnresolved(): void {
      if (!current() || state.status === 'accepted') return;
      unresolved = true; state = { status: 'unknown' };
    },
    getState(): ResourceCommandState {
      if (!current()) return { status: 'unavailable' };
      return state.status === 'accepted' ? { ...state, result: { ...state.result } } : { ...state };
    },
    getIntent(): ResourceCommand | null { return current() ? intent() : null; },
    send(signal?: AbortSignal): Promise<ResourceCommandResult> {
      if (!current() || signal?.aborted) return Promise.reject(new DOMException('Resource context changed', 'AbortError'));
      if (inFlight) return inFlight;
      if (state.status === 'accepted') return Promise.resolve({ ...state.result });
      inFlight = run(signal);
      return inFlight;
    },
  };
}
